import { useEffect, useRef, useState } from 'react';

/* ---------------- loading & empty states ---------------- */

export function Loader({ label = 'Loading…' }) {
  return (
    <div className="loader" role="status">
      <div className="loader-ring" />
      <p className="muted">{label}</p>
    </div>
  );
}

/**
 * A spinner sized to sit inside a button, beside the label.
 *
 * Draws itself in the button's own text colour and in em units, so it
 * belongs to whatever it is dropped into without being told about it.
 * Purely decorative - the button carries aria-busy, and the label it
 * sits next to says what is happening.
 */
export function Spinner() {
  return <span className="spin" aria-hidden="true" />;
}

export function Empty({ icon = '🔍', title, text, action }) {
  return (
    <div className="empty">
      <div className="empty-icon">{icon}</div>
      <h3>{title}</h3>
      {text && <p className="muted">{text}</p>}
      {action}
    </div>
  );
}

/* ---------------- messages ---------------- */

export function Alert({ kind = 'info', children, onDismiss }) {
  const icon = { info: 'ℹ️', good: '🎉', warn: '⚠️', bad: '😕' }[kind] ?? 'ℹ️';
  return (
    <div className={`alert alert-${kind}`} role={kind === 'bad' ? 'alert' : 'status'}>
      <span className="alert-icon" aria-hidden="true">{icon}</span>
      <div className="grow">{children}</div>
      {onDismiss && (
        <button type="button" className="alert-x" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  );
}

/** Auto-dismissing toast, anchored bottom-centre. */
export function Toast({ message, kind = 'good', onDone, duration = 3200 }) {
  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(onDone, duration);
    return () => clearTimeout(t);
  }, [message, duration, onDone]);

  if (!message) return null;
  return (
    <div className={`toast toast-${kind} anim-pop`} role="status">
      <span aria-hidden="true">{kind === 'bad' ? '😕' : '✅'}</span>
      <span>{message}</span>
    </div>
  );
}

/* ---------------- modal ---------------- */

export function Modal({ open, title, onClose, children, wide = false }) {
  const ref = useRef(null);
  // Hold the latest onClose in a ref so it is NOT an effect dependency.
  // If it were, every parent re-render (e.g. each keystroke in a form
  // field) would re-run the effect and call ref.focus(), stealing focus
  // from the input back to the dialog after a single character.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onCloseRef.current?.();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    ref.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        className={`modal ${wide ? 'modal-wide' : ''} anim-pop`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <div className="modal-head">
          <h3>{title}</h3>
          <button type="button" className="modal-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

/* ---------------- progress ---------------- */

export function ProgressBar({ value, max = 100, tone = 'mint', label }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={`pbar t-${tone}`}>
      <div className="pbar-track">
        <div className="pbar-fill" style={{ width: `${pct}%` }} />
      </div>
      {label !== false && <span className="pbar-label">{label ?? `${pct}%`}</span>}
    </div>
  );
}

export function ProgressRing({ value, max = 100, size = 92, tone = 'grape', children }) {
  const pct = max > 0 ? Math.min(1, value / max) : 0;
  const r = (size - 12) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className={`ring t-${tone}`} style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} className="ring-track" strokeWidth="10" fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          className="ring-fill"
          strokeWidth="10"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="ring-mid">{children ?? `${Math.round(pct * 100)}%`}</div>
    </div>
  );
}

/* ---------------- stat tile ---------------- */

export function Stat({ icon, value, label, tone = 'grape' }) {
  return (
    <div className={`stat t-${tone}`}>
      <span className="stat-icon" aria-hidden="true">{icon}</span>
      <div>
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  );
}

/* ---------------- confetti ---------------- */

const CONFETTI_COLOURS = ['#7c4dff', '#29b6f6', '#ff6b6b', '#22c58b', '#ffc93c', '#ff7eb6'];

/** Purely decorative celebration burst. Cleans itself up. */
export function Confetti({ run, pieces = 70 }) {
  const [bits, setBits] = useState([]);

  useEffect(() => {
    if (!run) {
      setBits([]);
      return undefined;
    }
    setBits(
      Array.from({ length: pieces }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 2.2 + Math.random() * 1.6,
        colour: CONFETTI_COLOURS[i % CONFETTI_COLOURS.length],
        size: 8 + Math.random() * 8,
        round: Math.random() > 0.5,
      })),
    );
    const t = setTimeout(() => setBits([]), 4200);
    return () => clearTimeout(t);
  }, [run, pieces]);

  if (!bits.length) return null;
  return (
    <div className="confetti" aria-hidden="true">
      {bits.map((b) => (
        <span
          key={b.id}
          style={{
            left: `${b.left}%`,
            width: b.size,
            height: b.size,
            background: b.colour,
            borderRadius: b.round ? '50%' : '3px',
            animationDelay: `${b.delay}s`,
            animationDuration: `${b.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

/* ---------------- misc ---------------- */

export function Avatar({ name, tone = 'grape', size = 44 }) {
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
  return (
    <span
      className={`avatar t-${tone}`}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

export function formatDate(value, withTime = false) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

export function relativeTime(value) {
  if (!value) return 'never';
  const diff = Date.now() - new Date(value).getTime();
  if (Number.isNaN(diff)) return 'never';
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatDate(value);
}
